import { useState, useEffect, useRef, useCallback } from 'react'
import { Search, X, Users, FileText, ArrowRightLeft, CheckSquare, Loader2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useT } from '../i18n/index.js'

// ── Types ──

interface SearchResult {
  id: string
  type: 'contact' | 'mandat' | 'transaction' | 'task'
  title: string
  subtitle?: string
  href: string
}

interface SearchResponse {
  contacts: SearchResult[]
  mandats: SearchResult[]
  transactions: SearchResult[]
  tasks: SearchResult[]
}

const TYPE_ICONS: Record<string, React.ElementType> = {
  contact: Users,
  mandat: FileText,
  transaction: ArrowRightLeft,
  task: CheckSquare,
}

// ── Hook — expose open state for KeyboardShortcuts integration ──

let _setOpen: ((v: boolean) => void) | null = null

export function openGlobalSearch() {
  _setOpen?.(true)
}

// ── Component ──

export function GlobalSearch() {
  const t = useT()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [activeIdx, setActiveIdx] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Register setter for external trigger
  useEffect(() => {
    _setOpen = setOpen
    return () => { _setOpen = null }
  }, [])

  // Flatten results for keyboard navigation
  const flatResults: SearchResult[] = results
    ? [
        ...results.contacts,
        ...results.mandats,
        ...results.transactions,
        ...results.tasks,
      ]
    : []

  // Ctrl+K shortcut
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(v => !v)
      }
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  // Focus input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50)
      setQuery('')
      setResults(null)
      setActiveIdx(-1)
    }
  }, [open])

  const search = useCallback(async (q: string) => {
    if (!q.trim()) { setResults(null); return }
    setLoading(true)
    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`)
      if (res.ok) {
        const data = await res.json() as SearchResponse
        setResults(data)
        setActiveIdx(-1)
      }
    } catch { /* ignore */ } finally {
      setLoading(false)
    }
  }, [])

  function handleInput(val: string) {
    setQuery(val)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => search(val), 200)
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx(i => Math.min(i + 1, flatResults.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault()
      const r = flatResults[activeIdx]
      if (r) { navigate(r.href); setOpen(false) }
    }
  }

  function resolveLabel(key: string): string {
    const parts = key.split('.')
    let val: unknown = t
    for (const p of parts) {
      if (val && typeof val === 'object' && p in (val as Record<string, unknown>)) {
        val = (val as Record<string, unknown>)[p]
      } else return key
    }
    return typeof val === 'string' ? val : key
  }

  function renderGroup(label: string, items: SearchResult[], startIdx: number) {
    if (!items.length) return null
    return (
      <div key={label}>
        <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-disabled)] bg-[var(--color-background)]">
          {label}
        </div>
        {items.map((item, i) => {
          const Icon = TYPE_ICONS[item.type] ?? FileText
          const idx = startIdx + i
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => { navigate(item.href); setOpen(false) }}
              onMouseEnter={() => setActiveIdx(idx)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 min-h-[44px] text-left transition-colors focus-visible:outline-none ${
                activeIdx === idx
                  ? 'bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                  : 'text-[var(--color-text)] hover:bg-[var(--color-surface-raised)]'
              }`}
              aria-selected={activeIdx === idx}
            >
              <Icon size={16} className="shrink-0 text-[var(--color-primary)]" />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{item.title}</p>
                {item.subtitle && (
                  <p className="text-xs text-[var(--color-text-secondary)] truncate">{item.subtitle}</p>
                )}
              </div>
            </button>
          )
        })}
      </div>
    )
  }

  if (!open) return null

  const cCount = results?.contacts.length ?? 0
  const mCount = results?.mandats.length ?? 0
  const txCount = results?.transactions.length ?? 0

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] bg-black/50"
      onClick={() => setOpen(false)}
      role="dialog"
      aria-modal="true"
      aria-label={resolveLabel('search.modalLabel')}
    >
      <div
        className="w-full max-w-xl bg-[var(--color-surface)] rounded-2xl shadow-2xl border border-[var(--color-border)] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--color-border)]">
          {loading
            ? <Loader2 size={18} className="shrink-0 text-[var(--color-text-disabled)] animate-spin" />
            : <Search size={18} className="shrink-0 text-[var(--color-text-disabled)]" />
          }
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={e => handleInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={resolveLabel('search.placeholder')}
            className="flex-1 bg-transparent text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-disabled)] outline-none"
            role="combobox"
            aria-expanded={flatResults.length > 0}
            aria-autocomplete="list"
            aria-activedescendant={activeIdx >= 0 ? `sr-${activeIdx}` : undefined}
          />
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-[var(--color-text-secondary)] hover:text-[var(--color-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
            aria-label={t.common.close}
          >
            <X size={16} />
          </button>
        </div>

        {/* Results */}
        {results && flatResults.length > 0 && (
          <div
            className="max-h-96 overflow-y-auto"
            role="listbox"
            aria-label={resolveLabel('search.results')}
          >
            {renderGroup(resolveLabel('search.groupContacts'), results.contacts, 0)}
            {renderGroup(resolveLabel('search.groupMandats'), results.mandats, cCount)}
            {renderGroup(resolveLabel('search.groupTransactions'), results.transactions, cCount + mCount)}
            {renderGroup(resolveLabel('search.groupTasks'), results.tasks, cCount + mCount + txCount)}
          </div>
        )}

        {results && flatResults.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-[var(--color-text-secondary)]">
            {resolveLabel('search.noResults')}
          </div>
        )}

        {!results && !loading && (
          <div className="px-4 py-4 text-xs text-[var(--color-text-disabled)] text-center">
            {resolveLabel('search.hint')}
          </div>
        )}

        {/* Footer hint */}
        <div className="border-t border-[var(--color-border)] px-4 py-2 flex items-center gap-4 text-[10px] text-[var(--color-text-disabled)]">
          <span><kbd className="px-1 py-0.5 rounded bg-[var(--color-background)] border border-[var(--color-border-subtle)] font-mono">↑↓</kbd> naviguer</span>
          <span><kbd className="px-1 py-0.5 rounded bg-[var(--color-background)] border border-[var(--color-border-subtle)] font-mono">Entrée</kbd> ouvrir</span>
          <span><kbd className="px-1 py-0.5 rounded bg-[var(--color-background)] border border-[var(--color-border-subtle)] font-mono">Échap</kbd> fermer</span>
        </div>
      </div>
    </div>
  )
}
